import { useState, useEffect } from "react";
import {
  BarChart3, Users, BookOpen, Shield, CheckCircle2, Clock,
  AlertTriangle, Target, Activity, TrendingUp, Award,
  Sparkles, Loader2, Building2, Download, ArrowUpRight,
  ArrowDownRight, GraduationCap, Heart, Zap,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart as RPieChart, Pie, Cell,
  RadarChart, PolarGrid, PolarAngleAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import api from "../../lib/api";
import { toast } from "sonner";
import { cn } from "../../lib/utils";

const COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b", "#ec4899", "#06b6d4"];

const KPI = ({ label, value, sub, icon: Icon, color, size = "default" }) => (
  <Card className="border shadow-none">
    <CardContent className={size === "lg" ? "p-5" : "p-4"}>
      <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center mb-2", color)}>
        <Icon className="w-4 h-4" />
      </div>
      <p className={cn("font-bold tabular-nums", size === "lg" ? "text-2xl" : "text-xl")}>{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
      {sub && <p className="text-[9px] text-muted-foreground mt-0.5">{sub}</p>}
    </CardContent>
  </Card>
);

const HealthBadge = ({ score }) => {
  const color = score >= 70 ? "bg-green-50 text-green-700 border-green-200" : score >= 40 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200";
  const label = score >= 70 ? "Healthy" : score >= 40 ? "At Risk" : "Critical";
  return <Badge className={cn("text-[9px] border", color)}>{score}% — {label}</Badge>;
};

const ExecutiveAnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiInsight, setAiInsight] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/analytics/executive-enhanced");
        setData(res.data);
      } catch { toast.error("Failed to load executive analytics"); }
      setLoading(false);
    })();
  }, []);

  const fetchAI = async () => {
    if (!data) return;
    setAiLoading(true);
    try {
      const res = await api.post("/analytics/ai-insight", {
        section: "executive_summary",
        metrics: data.executive_summary,
      });
      setAiInsight(res.data.insight);
    } catch { toast.error("AI insight failed"); }
    setAiLoading(false);
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/analytics/executive/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `executive_analytics_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Export downloaded");
    } catch { toast.error("Export failed"); }
  };

  if (loading) return (
    <AdminLayout title="Executive Analytics">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return null;
  const es = data.executive_summary || {};

  // Radar data for org health
  const radarData = [
    { metric: "Path Completion", value: es.paths?.rate || 0, fullMark: 100 },
    { metric: "Compliance", value: es.compliance?.rate || 0, fullMark: 100 },
    { metric: "Assessment Score", value: es.assessments?.avg_score || 0, fullMark: 100 },
    { metric: "Pass Rate", value: es.assessments?.pass_rate || 0, fullMark: 100 },
    { metric: "Engagement", value: Math.min(100, (es.total_learners || 1) * 10), fullMark: 100 },
  ];

  return (
    <AdminLayout title="Executive Analytics">
      <div className="space-y-5 max-w-[1400px]" data-testid="executive-analytics-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold">Executive Analytics</h1>
            <p className="text-sm text-muted-foreground">C-level insights — learning paths, compliance, assessments</p>
          </div>
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={fetchAI} disabled={aiLoading} data-testid="ai-exec-btn">
              {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI Summary
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleExport} data-testid="export-btn">
              <Download className="w-3 h-3" /> Export CSV
            </Button>
          </div>
        </div>

        {/* AI Insight */}
        {aiInsight && (
          <Card className="border-2 border-violet-200 bg-violet-50/30 shadow-none" data-testid="ai-exec-insight">
            <CardContent className="p-4"><div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /><div className="text-sm whitespace-pre-line">{aiInsight}</div></div></CardContent>
          </Card>
        )}

        {/* Executive KPIs */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KPI label="Total Learners" value={es.total_learners} icon={Users} color="text-blue-600 bg-blue-50" size="lg" />
          <KPI label="Path Completion" value={`${es.paths?.rate}%`} icon={Target} color="text-green-600 bg-green-50" sub={`${es.paths?.completed}/${es.paths?.assigned} completed`} />
          <KPI label="Compliance Rate" value={`${es.compliance?.rate}%`} icon={Shield} color={es.compliance?.overdue > 0 ? "text-red-600 bg-red-50" : "text-teal-600 bg-teal-50"} sub={`${es.compliance?.overdue} overdue`} />
          <KPI label="Avg Assessment Score" value={`${es.assessments?.avg_score}%`} icon={Award} color="text-violet-600 bg-violet-50" sub={`${es.assessments?.pass_rate}% pass rate`} />
          <KPI label="Assessment Attempts" value={es.assessments?.attempts} icon={GraduationCap} color="text-amber-600 bg-amber-50" />
        </div>

        {/* Org Health Radar + Learning Paths */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Org Health */}
          <Card className="border shadow-none" data-testid="org-health-radar">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Organization Health</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 9 }} />
                  <Radar name="Score" dataKey="value" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Learning Paths */}
          <Card className="border shadow-none lg:col-span-2" data-testid="learning-paths-chart">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Learning Path Performance</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.learning_paths?.paths?.slice(0, 6)} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="title" type="category" tick={{ fontSize: 9 }} width={140} />
                  <Tooltip />
                  <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                  <Bar dataKey="completed" fill="#22c55e" name="Completed" barSize={10} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="in_progress" fill="#3b82f6" name="In Progress" barSize={10} radius={[0, 3, 3, 0]} />
                  <Bar dataKey="dropped" fill="#ef4444" name="Dropped" barSize={10} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Compliance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Compliance by Template */}
          <Card className="border shadow-none" data-testid="compliance-templates">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Shield className="w-4 h-4 text-teal-600" /> Compliance Programs</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="space-y-2">
                {data.compliance?.templates?.slice(0, 8).map(t => (
                  <div key={t.id} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{t.title}</p>
                      <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1">
                        <div className="bg-teal-500 h-1.5 rounded-full transition-all" style={{ width: `${t.rate}%` }} />
                      </div>
                    </div>
                    <Badge className={cn("text-[9px] border shrink-0", t.rate >= 80 ? "bg-green-50 text-green-700" : t.overdue > 0 ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700")}>
                      {t.rate}%
                    </Badge>
                    {t.overdue > 0 && <span className="text-[9px] text-red-500 shrink-0">{t.overdue} overdue</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Compliance Deadlines */}
          <Card className="border shadow-none" data-testid="compliance-deadlines">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Upcoming Compliance Deadlines</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={[
                  { period: "Next 7d", count: data.compliance?.deadlines?.next_7 || 0 },
                  { period: "Next 30d", count: data.compliance?.deadlines?.next_30 || 0 },
                  { period: "Next 60d", count: data.compliance?.deadlines?.next_60 || 0 },
                  { period: "Next 90d", count: data.compliance?.deadlines?.next_90 || 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} barSize={40} name="Assignments Due" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Assessment Analysis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="border shadow-none" data-testid="assessment-performance">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Assessment Performance</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {["Assessment", "Attempts", "Avg Score", "Pass Rate"].map(h => (
                      <th key={h} className="p-1.5 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {data.assessments?.assessments?.slice(0, 10).map(a => (
                      <tr key={a.id} className="border-b hover:bg-muted/20">
                        <td className="p-1.5 text-xs font-medium">{a.title?.slice(0, 35)}</td>
                        <td className="p-1.5 text-xs">{a.attempts}</td>
                        <td className="p-1.5"><Badge className={cn("text-[9px]", a.avg_score >= 70 ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700")}>{a.avg_score}%</Badge></td>
                        <td className="p-1.5"><Badge className={cn("text-[9px]", a.pass_rate >= 70 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700")}>{a.pass_rate}%</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Score Distribution */}
          <Card className="border shadow-none" data-testid="exec-score-distribution">
            <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Score Distribution</CardTitle></CardHeader>
            <CardContent className="px-2 pb-3">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.assessments?.score_distribution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={36} name="Students">
                    {data.assessments?.score_distribution?.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Tenant Health Scorecards */}
        {data.tenant_health?.length > 0 && (
          <Card className="border shadow-none" data-testid="tenant-health">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Heart className="w-4 h-4 text-rose-600" /> Tenant Health Scorecards</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.tenant_health.map(t => (
                  <div key={t.tenant_id} className={cn("p-4 rounded-lg border-2", t.risk === "low" ? "border-green-200 bg-green-50/20" : t.risk === "medium" ? "border-amber-200 bg-amber-50/20" : "border-red-200 bg-red-50/20")}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-bold">{t.tenant_name}</p>
                      <HealthBadge score={t.health_score} />
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <div><p className="text-lg font-bold">{t.engagement}%</p><p className="text-[9px] text-muted-foreground">Engagement</p></div>
                      <div><p className="text-lg font-bold">{t.completion_rate}%</p><p className="text-[9px] text-muted-foreground">Completion</p></div>
                      <div><p className="text-lg font-bold">{t.compliance_rate}%</p><p className="text-[9px] text-muted-foreground">Compliance</p></div>
                    </div>
                    <p className="text-[9px] text-muted-foreground mt-2">{t.learners} learners — {t.mau} MAU</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Department Performance by Path */}
        <Card className="border shadow-none" data-testid="dept-path-performance">
          <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Department × Learning Path Performance</CardTitle></CardHeader>
          <CardContent className="px-5 pb-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b bg-muted/30">
                  {["Department", "Assigned", "Completed", "In Progress", "Rate"].map(h => (
                    <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {data.learning_paths?.department_breakdown?.map(d => (
                    <tr key={d.department} className="border-b hover:bg-muted/20">
                      <td className="p-2 text-sm font-medium">{d.department}</td>
                      <td className="p-2 text-sm">{d.total}</td>
                      <td className="p-2 text-sm">{d.completed}</td>
                      <td className="p-2 text-sm">{d.in_progress}</td>
                      <td className="p-2"><Badge className={cn("text-[9px]", d.rate >= 70 ? "bg-green-50 text-green-700" : d.rate >= 40 ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700")}>{d.rate}%</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default ExecutiveAnalyticsPage;
